// hooks/useCreateNewReservation.ts
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store"; // Adjust the import based on your file structure
import { CompleteStripePaymentData, CompleteVoucherStripePaymentData } from "../../api/types";
import Logger from "../helpers/logger";
import ErrorTracker from "../helpers/errorTracker";
import {
  paymentCompleted,
  paymentCompletionBegan,
  paymentCompletionFailed,
  subscriptionCompleted,
} from "../slice/bookingCreation/bookingCreationSlice";

import { useCompleteSubscriptionPaymentMutation, useCompleteVoucherStripePaymentMutation } from "../../api/payments/paymentsApiSlice";
import { useCallback } from "react";
import { enqueueNotification } from "../slice/notification/notificationSlice";
import { t } from "i18next";
import KolulaStorageManager from "storage/KolulaStorageManager";

export const useCompleteSubscriptionPayment = () => {
  const contact = useSelector(
    (state: RootState) => state.bookingCreation.personalData
  );

  const indexDBStore = new KolulaStorageManager();

  const [completeSubscriptionPaymentMutation] = useCompleteSubscriptionPaymentMutation();
  const dispatch = useDispatch();

  // Use useCallback to memoize the completeStripePayment function
  return useCallback(
    async (sessionId: string) => {
      dispatch(paymentCompletionBegan());

      try {
        if (sessionId && contact) {
          const payload: CompleteVoucherStripePaymentData = {
            sessionId: sessionId,
          };

          const response = await completeSubscriptionPaymentMutation(
            payload
          ).unwrap();

          if (response.customerAgreementId) {
            await indexDBStore.deletePersonalData();
            await indexDBStore.insertSubscriptionId(
              response.customerAgreementId
            );

            dispatch(subscriptionCompleted(response.customerAgreementId));
            dispatch(
              enqueueNotification({
                severity: "success",
                message: t("book_location.payment.success"),
              })
            );
          }

          return response;
        }
      } catch (error) {
        dispatch(paymentCompletionFailed());
        dispatch(
          enqueueNotification({
            severity: "error",
            message: t("book_location.payment.error_voucher"),
          })
        );

        Logger.for("Complete Voucher Payment").error(error);
        ErrorTracker.trackException(error as Error);
        // Handle the error state or dispatch error actions if necessary
      }
    },
    [contact, completeSubscriptionPaymentMutation, dispatch]
  );
};
